import type { DesktopDatePickerProps } from '@mui/x-date-pickers'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export type DesktopDatePickerControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = Omit<DesktopDatePickerProps, 'name'> & {
  name: TName
  // It enables showing field validation errors. Its default value is true
  showError?: boolean
  controllerProps?: Omit<ControllerProps<TFieldValues, TName>, 'render' | 'name'>
}

function DesktopDatePickerController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  showError = true,
  controllerProps,
  slotProps,
  ...fieldProps
}: DesktopDatePickerControllerProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>()
  const { i18n } = useTranslation()

  return (
    <Controller
      control={control}
      name={name}
      {...controllerProps}
      render={({ field: { value, ...field }, fieldState: { error, invalid } }) => (
        <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
          <DesktopDatePicker
            enableAccessibleFieldDOMStructure={false}
            {...field}
            slotProps={{
              ...slotProps,
              textField: {
                ...slotProps?.textField,
                error: showError && invalid,
                helperText: error?.message,
              },
            }}
            value={value ?? null}
            {...fieldProps}
          />
        </LocalizationProvider>
      )}
    />
  )
}

export default DesktopDatePickerController
